import { defineRecipe, SystemStyleObject } from '@chakra-ui/react';

/*
 * Design system source: .claude/skills/monetizenow-design/preview/buttons.html
 * Primitives reference: .claude/skills/monetizenow-design/ui_kits/app/Primitives.jsx
 *
 * Variant mapping from design system → legacy Chakra:
 *   primary       → primary (same)
 *   secondary     → white bg + indigo border + indigo text. Subordinate to
 *                   primary's solid fill but still draws attention on white
 *                   surfaces (toolbars, table headers) where the neutral style
 *                   disappears.
 *   neutral (NEW) → white bg + hairline gray border. The "quiet" choice for
 *                   destructive-adjacent actions (Cancel) or low-importance
 *                   toolbar buttons.
 *   cancel        → aliased to neutral. Cancel is always quiet.
 *   tertiary      → tertiary (transparent + indigo text)
 *   plain         → aliased to tertiary (legacy callers like
 *                   MTertiaryArrowButton continue to work)
 *   danger        → delete
 *   danger-outline→ deleteOutline
 *   success       → success
 *   icon, link    → kept as-is (utility variants)
 *   ai            → magenta tint, reserved for MonetizeIQ entry points
 *   closeButton   → v3-only addition, see note below
 *
 * Removed in V2:
 *   tertiaryDestructive → use `delete` (call sites already migrated)
 *
 * `closeButton`: v3's real `CloseButton` component is a thin wrapper over
 * `IconButton` (`variant="ghost"`) with no dedicated recipe of its own —
 * there is no `closeButton` key anywhere in v3's recipe system, so the old
 * standalone `CloseButton.ts` theme file (same dead-code shape as every
 * other Track I discovery: called `defineStyleConfig` without importing it,
 * never wired into `theme.ts`) couldn't be ported the normal way. Its
 * styling lives here instead as its own variant, explicitly requested by
 * `MCloseButton.tsx` (`variant="closeButton"`) rather than relying on v3's
 * default `variant="ghost"` — keeps every other real `ghost`-variant
 * IconButton in the app unaffected. Per-size box dimensions (w/h/fontSize,
 * distinct from the text-button `size` scale above) live in
 * `compoundVariants` below, gated on `variant: 'closeButton'` so they don't
 * leak onto `primary`/`secondary`/etc.
 */

/*
 * v3's built-in sizes also size child SVGs via `_icon` (`& :where(svg)`,
 * specificity 0,1,0). That ties with an icon's own `boxSize` class, so
 * whichever rule is inserted last wins and `<MIcon boxSize="4">` in a button
 * can render at the button's 20px. `null` drops the built-in rule (it
 * survives the config merge, where only `undefined` is skipped, and emits
 * nothing). The `*:where(&)` restatement has zero specificity, so it still
 * sizes bare SVGs such as `CloseButton`'s default icon while an icon's own
 * size always wins. The style types allow neither `null` nor a selector that
 * doesn't start or end with `&`, hence the cast.
 */
const iconSize = (size: number) =>
  ({
    _icon: { width: null, height: null },
    '*:where(&) :where(svg)': { width: size, height: size },
  }) as unknown as SystemStyleObject;

const neutralStyle = {
  bg: 'tWhite.base',
  borderColor: 'border.subtle',
  color: 'fg.primary',
  _hover: {
    bg: 'bg.hover',
    borderColor: 'border.default',
    color: 'fg.primary',
  },
  _active: {
    bg: 'bg.pressed',
  },
  _disabled: {
    bg: 'bg.muted',
    borderColor: 'border.subtle',
    color: 'fg.muted',
    cursor: 'not-allowed',
    _hover: {
      bg: 'bg.muted !important',
      borderColor: 'border.subtle !important',
      color: 'fg.muted !important',
    },
  },
};

const tertiaryStyle = {
  bg: 'transparent',
  border: 'none',
  color: 'tIndigo.base',
  _hover: {
    bg: 'tBlue.hover',
    color: 'tIndigo.base',
  },
  _active: {
    bg: 'tBlue.lightShade',
  },
  _disabled: {
    color: 'fg.muted',
    cursor: 'not-allowed',
    _hover: { bg: 'transparent !important', color: 'fg.muted !important' },
  },
};

export const buttonTheme = defineRecipe({
  base: {
    lineHeight: '1.2',
    borderRadius: 'sm',
    fontWeight: 500,
    letterSpacing: '-0.003em',
    transitionProperty: 'colors',
    transitionDuration: 'normal',
    // Buttons rendered with `asChild` onto a Link would otherwise inherit
    // Link's hover underline. Buttons should never underline — the bg/color
    // hover state is already the affordance.
    textDecoration: 'none',
    _hover: { textDecoration: 'none' },
    _focus: { textDecoration: 'none' },
    // Keyboard focus ring — design-system 3px indigo halo. Mouse-click focus
    // (`_focus` without `_focus-visible`) intentionally has no ring so click
    // targets don't flash on every press.
    _focusVisible: {
      boxShadow: 'focus',
      outline: 'none',
      textDecoration: 'none',
    },
    // Loading state — Chakra adds data-loading and blocks click events, but the
    // hover/focus pointer affordance still triggers visually. Force not-allowed
    // cursor and suppress hover transitions while loading so the button reads as
    // "busy, leave me alone."
    '&[data-loading]': {
      cursor: 'not-allowed',
      pointerEvents: 'auto', // keep tooltip on hover, but click is no-op via Chakra
    },
  },
  variants: {
    variant: {
      primary: {
        bg: 'tIndigo.base',
        color: 'white',
        _hover: {
          bg: 'tIndigo.600',
          color: 'white',
          _disabled: {
            bg: 'tGray.lightPurple',
            color: 'tGray.darkGrayPurple',
          },
        },
        _active: {
          bg: 'tIndigo.700',
        },
        _disabled: {
          opacity: 1,
          bg: 'tGray.lightPurple',
          color: 'tGray.darkGrayPurple',
        },
      },
      secondary: {
        bg: 'tWhite.base',
        border: '1px solid',
        borderColor: 'tIndigo.base',
        color: 'tIndigo.base',
        _hover: {
          bg: 'tBlue.hover',
          borderColor: 'tIndigo.600',
          color: 'tIndigo.base',
        },
        _active: {
          bg: 'tBlue.lightShade',
          borderColor: 'tIndigo.700',
        },
        // Matches primary's disabled fill so paired buttons disable identically
        _disabled: {
          opacity: 1,
          bg: 'tGray.lightPurple',
          borderColor: 'tGray.lightPurple',
          color: 'tGray.darkGrayPurple',
          cursor: 'not-allowed',
          _hover: {
            bg: 'tGray.lightPurple !important',
            borderColor: 'tGray.lightPurple !important',
            color: 'tGray.darkGrayPurple !important',
          },
        },
      },
      neutral: neutralStyle,
      cancel: neutralStyle,
      tertiary: tertiaryStyle,
      // Alias — `plain` keeps working for legacy call sites (MTertiaryArrowButton
      // etc.) and renders the same as `tertiary`.
      plain: tertiaryStyle,
      success: {
        bg: 'tGreen.approval',
        color: 'white',
        _hover: {
          bg: 'tGreen.approvalHover',
          color: 'white',
          _disabled: {
            bg: 'tGray.lightPurple',
            color: 'tGray.darkGrayPurple',
          },
        },
        _focusVisible: {
          boxShadow: 'focusSuccess',
        },
        _disabled: {
          opacity: 1,
          bg: 'tGray.lightPurple',
          color: 'tGray.darkGrayPurple',
        },
      },
      icon: {
        // `fg.strong` (~plum/near-black) reads as the canonical toolbar icon
        // color across the app — picked over `fg.muted` because the app's
        // icon glyphs are dense and the muted gray makes them feel washed
        // out at small sizes.
        color: 'fg.strong',
        background: 'none',
        // No bg shift on _focus / _active — the keyboard halo (base._focusVisible)
        // signals focus, and consumers with their own bg (FAB, AI chat send
        // button, etc.) own their own active styling via inline props. Hover
        // only shifts the background; color stays put because there isn't a
        // meaningfully darker shade than fg.strong to flip to.
        _hover: {
          background: 'bg.hover',
          color: 'fg.strong',
        },
        _disabled: {
          color: 'fg.muted',
          opacity: 0.5,
          cursor: 'not-allowed',
          _focus: { background: 'none' },
          _active: { background: 'none' },
          _hover: { background: 'none', color: 'fg.muted' },
        },
      },
      delete: {
        bg: 'tRed.base',
        color: 'white',
        _hover: {
          bg: 'tRed.hover',
          color: 'white',
        },
        _focusVisible: {
          boxShadow: 'focusDanger',
        },
        _disabled: {
          bg: 'tGray.darkPurple',
          color: 'tGray.lightPurple',
          _hover: {
            bg: '#606070 !important',
            color: 'tGray.lightPurple !important',
          },
        },
      },
      deleteOutline: {
        bg: 'white',
        color: 'tRed.base',
        border: '1px solid',
        borderColor: 'tRed.base',
        borderRadius: 'sm',
        _hover: {
          bg: 'tRed.50',
          color: 'tRed.base',
        },
        _focusVisible: {
          boxShadow: 'focusDanger',
        },
        _disabled: {
          border: 'none',
          bg: 'tGray.darkPurple',
          color: 'tGray.lightPurple',
          _hover: {
            bg: 'tGray.darkPurple !important',
            color: 'tGray.lightPurple !important',
          },
        },
      },
      link: {
        fontWeight: 400,
        bg: 'transparent',
        border: 'none',
        color: 'tIndigo.base',
        padding: 0,
        margin: 0,
        minW: 'initial',
        _hover: {
          textDecoration: 'underline',
        },
        _focus: {
          textDecoration: 'underline',
        },
        _disabled: {
          color: 'fg.muted',
          cursor: 'not-allowed',
          _hover: { textDecoration: 'none' },
        },
      },
      // NEW — design-system "ai" variant. Reserved for MonetizeIQ / AI entry points.
      ai: {
        bg: 'tMagenta.50',
        color: 'tMagenta.base',
        border: 'none',
        _hover: {
          bg: '#ECE0FF',
          color: 'tMagenta.base',
          _disabled: {
            bg: 'tGray.lightPurple',
            color: 'tGray.darkGrayPurple',
          },
        },
        // Active = a darker tint of the resting bg, not the pale lilac
        // tMagenta.200 (#BAA1E3) which reads as almost-white on a white surface.
        _active: {
          bg: '#DCC7FF',
        },
        _focusVisible: {
          boxShadow: 'focusAi',
        },
        _disabled: {
          opacity: 1,
          bg: 'tGray.lightPurple',
          color: 'tGray.darkGrayPurple',
        },
      },
      closeButton: {
        minW: 'initial',
        bg: 'transparent',
        border: 'none',
        color: 'fg.muted',
        _hover: {
          backgroundColor: 'bg.hover',
          _disabled: {
            backgroundColor: 'transparent',
          },
        },
        _focusVisible: {
          boxShadow: 'focus',
          outline: 'none',
        },
      },
      // Not a design-system look — omit from the variant-mapping table above.
      // Pure escape hatch for a handful of pre-existing, fully hand-styled
      // buttons (their own border/bg/_hover/_disabled overrides) that used
      // v2's built-in `unstyled` variant, which v3 dropped entirely. Named
      // `bare` rather than `unstyled` on purpose — this directory's own
      // guidance says `unstyled` shouldn't be used in app code.
      bare: {},
    },
    // Sizes — design system spec: md=30px, sm=26px, lg=36px. All share sm
    // (4px) radius from `base` for consistency across the button family.
    size: {
      lg: {
        h: '36px',
        minW: '150px',
        fontSize: 'lg', // 14px
        px: '16px',
        ...iconSize(5),
      },
      md: {
        h: '30px',
        minW: '120px',
        fontSize: 'sm', // 13px
        px: '12px',
        ...iconSize(5),
      },
      sm: {
        h: '30px',
        minW: '30px',
        fontSize: 'sm', // 12px
        px: '10px',
        ...iconSize(4),
      },
      xs: {
        h: '26px',
        minW: '26px',
        fontSize: 'xs', // 11px
        px: '8px',
        ...iconSize(4),
      },
    },
  },
  // `closeButton`'s own w/h/fontSize scale — distinct from the text-button
  // `size` values above, matching the old standalone `CloseButton.ts` file's
  // sizes exactly (port, not redesign). Gated to `variant: 'closeButton'`
  // via a compound variant so `primary`/`secondary`/etc. at the same `size`
  // are unaffected.
  compoundVariants: [
    {
      variant: 'closeButton',
      size: 'sm',
      css: { w: 3, h: 4, fontSize: '10px', p: 3 },
    },
    {
      variant: 'closeButton',
      size: 'md',
      css: { w: 6, h: 6, fontSize: '12px' },
    },
    {
      variant: 'closeButton',
      size: 'lg',
      css: { w: 5, h: 5, fontSize: '1.25rem' },
    },
  ],
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

export default buttonTheme;
